import React, { useRef, useState, useEffect } from 'react';
import 'styles/main.css';

import { animated, useSpring } from 'react-spring';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment-timezone';
import { pdfjs } from 'react-pdf';

import smoothscroll from 'smoothscroll-polyfill';
import update from 'immutability-helper';

import About from 'views/site/About.js';
import API from 'files/api.js';
import { NetworkSolutions } from 'managers/Home.js';
import Alert from 'views/Alert.js';
import AlertStack from 'views/AlertStack.js';
import Appearance from 'styles/Appearance.js';
import { BackgroundCheckInformationRequest, CertificationTestOnboarding, TraineeSignup } from 'managers/Users.js';
import Brand from 'styles/Brand.js';
import Button from 'views/Button.js';
import Catalog from 'files/Catalog.js';
import Contact from 'views/site/Contact.js';
import { CustomGlobalDataSurvey, EmergencyContactOnboardingSurvey, LeadGenerationSurvey, RecruitingSurvey } from 'managers/GlobalData.js';
import { EndIndex } from 'structure/Layer.js';
import DatePicker from 'views/DatePicker.js';
import Events from 'managers/Events.js';
import Footer from 'structure/Footer.js';
import Header from 'structure/Header.js';
import Home from 'views/site/Home.js';
import AppNavigation from 'files/AppNavigation.js';
import LeadQuizModal from 'views/site/LeadQuizModal.js';
import OmniShieldApp from 'views/site/OmniShieldApp.js';
import { PrivacyPolicy, TermsAndConditions } from 'managers/Privacy.js';
import Products from 'views/site/Products.js';
import QueryString from 'query-string';
import QuizModal from 'views/site/QuizModal.js';
import Quote from 'classes/Quote.js';
import QuoteDrawer from 'views/site/QuoteDrawer.js';
import QuoteRequestModal from 'views/site/QuoteRequestModal.js';
import Request from 'files/Request.js';
import Reviews from 'views/site/Reviews.js';
import ConsentModal from 'views/site/ConsentModal.js';
import Route from 'files/Route.js';
import SafetyCheck from 'views/site/SafetyCheck.js';
import Sheet from 'views/Sheet.js';
import AffiliationBanner from 'views/site/AffiliationBanner.js';
import { Support } from 'managers/Graci.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

const EventsManager = Events.new();
const Stripe = loadStripe(API.stripe);

// Views that render the new marketing surface. They get the light, white
// ground and the storefront design system; everything else is a legacy panel
// and keeps drawing with Appearance.
const MarketingViews = ['home', 'products', 'contact', 'about', 'safety_check', 'testimonials'];

// Routes that have moved. The products view replaced the network page, and
// links to ?route=network are already in the wild.
const RouteAliases = {
    network: 'products'
};

// index.php switches on `route` to build the Open Graph tags, and several
// views read their own deep-link parameters from the query string. These are
// the routes that address a view directly rather than a waypoint on the home
// page.
const DirectRoutes = ['app', 'background_checks', 'certification_test_onboarding', 'emergency_contact_onboarding_survey', 'global_data_survey', 'privacy', 'safety_check', 'trainee_signup', 'terms_and_conditions'];

// import pdf worker for inline pdf rendering
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const panels = {
    // The home page is now a single composition root rather than a list of
    // panels — sections are ordered inside views/site/Home.js, where the
    // reading order of the page is easier to see and to change.
    home: [{
        id: 'home',
        Component: Home
    }],
    app: [{
        id: 'app',
        Component: OmniShieldApp
    }],
    about: [{
        id: 'about',
        Component: About
    }],
    // The Nextdoor ad landing page. A DirectRoute so it carries its own query
    // parameters - the creative variant and Nextdoor's neighbourhood macros.
    safety_check: [{
        id: 'safety_check',
        Component: SafetyCheck
    }],
    contact: [{
        id: 'contact',
        Component: Contact
    }],
    products: [{
        id: 'products',
        Component: Products
    }],
    background_checks: [{
        id: 'background_check_information_request',
        Component: BackgroundCheckInformationRequest
    }],
    careers: [{
        id: 'recruiting_survey',
        Component: RecruitingSurvey,
        visible: utils => {
            return utils.dealership.get()?.recruiting_survey ? true : false;
        }
    }],
    global_data_survey: [{
        id: 'custom_global_data_survey',
        Component: CustomGlobalDataSurvey
    }],
    emergency_contact_onboarding_survey: [{
        id: 'emergency_contact_onboarding_survey',
        Component: EmergencyContactOnboardingSurvey
    }],
    network: [{
        id: 'network',
        Component: NetworkSolutions
    }],
    privacy: [{
        id: 'privacy_policy',
        Component: PrivacyPolicy
    }],
    promotions: [{
        id: 'lead_generation_survey',
        Component: LeadGenerationSurvey,
        visible: utils => {
            return utils.dealership.get()?.lead_generation_survey ? true : false;
        }
    }],
    terms_and_conditions: [{
        id: 'terms_and_conditions',
        Component: TermsAndConditions
    }],
    testimonials: [{
        id: 'testimonials',
        Component: Reviews
    }],
    trainee_signup: [{
        id: 'trainee_signup',
        Component: TraineeSignup
    }],
    certification_test_onboarding: [{
        id: 'certification_test_onboarding',
        Component: CertificationTestOnboarding
    }],
    support: [{
        id: 'support',
        Component: Support
    }]
}

const SupportRoutes = {
    ask_a_question: { flow: true },
    guides: { scroll: 'guides' },
    mobile_app_support: { flow: 'app' },
    product_manuals: { scroll: 'product-manuals' },
    troubleshooting: { flow: 'app' }
};

const App = () => {

    const activeRef = useRef({});
    const [appNavigation, setAppNavigation] = useState(AppNavigation.get());
    const dealershipRef = useRef(null);
    const layersRef = useRef(null);
    const omnishieldPreferencesRef = useRef(null);

    const [active, setActive] = useState({ view: null });
    const [alerts, setAlerts] = useState([]);
    const [contentAnimations, setContentAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 1,
        top: 0,
    }));
    const [datePicker, setDatePicker] = useState(null);
    const [dealership, setDealership] = useState(null);
    const [layers, setLayers] = useState([]);
    const [layerIndex, setLayerIndex] = useState([]);
    const [loader, setLoader] = useState(null);
    const [omnishieldPreferences, setOmnishieldPreferences] = useState(null);
    const [preflight, setPreflight] = useState(false);
    const [quizOpen, setQuizOpen] = useState(false);

    /*
     * The blind spot checker, opened over whatever page the visitor landed on.
     * `?quiz=1` in the ad's url is what opens it, so the ad points at the normal
     * page and the walkthrough comes up over it.
     */
    const [leadQuiz, setLeadQuiz] = useState(false);
    const [quote, setQuote] = useState({ count: 0, items: [], quiz: null });
    const [quoteModalOpen, setQuoteModalOpen] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [nonce] = useState(moment().unix());
    const [sheet, setSheet] = useState(null);
    const [size, setSize] = useState({ height: window.innerHeight, width: window.innerWidth });
    const [theme, setTheme] = useState('light');
    const [waypoint, setWaypoint] = useState(null);

    const onActiveViewChange = previous => {

        // scroll to top of page
        if(previous.view !== active.view) {
            window.scrollTo({ top: 0 });
        }

        // update page title if dealership is available
        if(dealership) {
            document.title = active.view !== 'home' ? `${dealership.name}: ${getTitleForView(active.view)}` : dealership.name;
        }

        // determine if the document should scroll to a waypoint, set a timeout if the view has just changed
        if(waypoint) {
            setTimeout(() => {
                let el = document.getElementById(`navigation_component_${waypoint}`);
                if(el) {
                    window.scrollTo({ 
                        behavior: 'smooth',
                        top: el.offsetTop
                    });
                }
            }, previous.view !== active.view ? 250 : 0);
        }

        // update active ref for next navigation change
        activeRef.current = active;
    }

    
    const onCloseLayer = layerID => {

        // filter layer id out of layer indexes
        setLayerIndex(layerIndex => {
            return update(layerIndex, {
                $apply: ids => ids.filter(id => id !== layerID)
            });
        });

        setLayers(layers => {

            // update layer and set visible flag to false
            let next = update(layers, {
                $apply: layers => layers.map(layer => {
                    if(layer.id === layerID) {
                        layer.visible = false;
                    }
                    return layer;
                })
            });

            // determine if at least one layer is still visible
            let targets = next.filter(layer => layer.visible !== false);
            if(targets.length === 0) {
                console.log('layers reset');
                document.body.style.overflowY = 'scroll';
                return [];
            }
            return next;
        });
    }

    const onLayerReposition = ({ id, position }) => {

        let index = layers.findIndex(layer => id === layer.id);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers => {
            return update(layers, {
                [index]: {
                    position: {
                        $set: position
                    }
                }
            });
        });
    }

    const onNavigationItemClick = item => {

        // a parent item only reveals its own children; the header owns that
        if(item.items?.length > 0) {
            return;
        }

        // The URL is the source of truth. Writing it here rather than setting
        // state directly means a navigation click and a browser back button
        // both arrive at onRouteChange, so there is only one path that can
        // change the active view.
        Route.push(item.waypoint || item.view, item.params);
    }

    const onAddToQuote = product => {
        Quote.addItem(product.handle, 1);
        setQuoteOpen(true);
    }

    const onAppSignOut = () => {

        // the app owns the session - this only asks. it answers with a
        // navigationChanged carrying authenticated: false, which is what empties
        // the account menu.
        // only asks. the app owns the session, and it answers with
        // signOutComplete once its own teardown has finished - navigating now
        // would tear the frame down mid-flight and leave the session behind.
        AppNavigation.requestSignOut();
    }

    const onQuoteClick = () => {
        setQuoteOpen(true);
    }

    const onOpenQuiz = () => {
        setQuizOpen(true);
    }

    // The walkthrough hands over a whole plan at once. The answers travel with
    // it so the dealership arrives already knowing the house.
    const onAddPlanToQuote = (plan, answers) => {
        plan.devices.forEach(device => Quote.setItem(device.handle, device.quantity));
        Quote.setQuiz(answers);
        setQuizOpen(false);
        setQuoteOpen(true);
    }

    const onRequestQuote = () => {
        setQuoteOpen(false);
        setQuoteModalOpen(true);
    }

    // The list has been handed off — clearing it stops a second visit from
    // resubmitting a quote the dealership is already working on.
    const onQuoteSubmitted = () => {
        Quote.clear();
    }

    const onRouteChange = ({ params, route }) => {
        let next = getStateForRoute(route);
        setActive({
            params: {
                ...params,
                support_flow: next.supportFlow || null,
                support_scroll: next.supportScroll || null
            },
            view: next.view
        });
        setWaypoint(next.waypoint);
    }

    const onOpenLayer = nextLayer => {

        if(layers.find(layer => {
            return layer.id === nextLayer.id && layer.visible !== false
        })) {
            utils.alert.show({
                title: nextLayer.abstract ? nextLayer.abstract.getTitle() : 'Just a Second',
                message: `There is already a window open for ${nextLayer.abstract ? `"${nextLayer.abstract.getTitle()}"` : 'this information'}`
            });
            return;
        }

        setTimeout(() => {
            setLayers(layers => update(layers, {
                $push: [nextLayer]
            }))
            setLayerIndex(layerIndex => update(layerIndex, {
                $unshift: [nextLayer.id]
            }))
        }, 0)
    }

    const onSetLayerIndex = layerID => {

        let index = layers.findIndex(layer => layer.id === layerID);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers => {
            return update(layers, {
                $apply: layers => layers.map(layer => {
                    layer.moveToFront = layer.id === layerID;
                    return layer;
                })
            });
        })

        let _index = layerIndex.findIndex(id => id === layerID);
        setLayerIndex(layerIndex => {
            return update(layerIndex, {
                $splice: [
                    [_index, 1],
                    [0, 0, layerID]
                ]
            });
        });
    }

    const onSetStyleSheetProperties = () => {
        setTheme(window.theme);
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        document.querySelector('meta[name="theme-color"]').setAttribute("content", Theme.colors.white);
    }

    const onSizeChange = () => {
        setSize({ height: window.innerHeight, width: window.innerWidth });
    }



    const getLayers = () => {
        return layers.map(({ id, abstract, options, title, visible, Component }, index) => {

            if(visible === false) {
                // must return null instead of using filter
                // using filter for visible does not preseve other visible layers
                return null;
            }
            return (
                <Component
                abstract={abstract}
                key={index}
                index={index}
                title={title}
                utils={utils}
                options={{
                    ...options,
                    index: index,
                    onClose: onCloseLayer,
                    onReposition: onLayerReposition,
                    onSetLayerIndex: onSetLayerIndex,
                    zIndex: EndIndex - layerIndex.findIndex(indexID => id === indexID)
                }}/>
            )
        });
    }

    const getMainContent = () => {

        // no additional logic is required if preflight tasks have not been completed
        if(preflight === false) {
            return null;
        }

        // return a placeholder if no dealership has been set
        if(!dealership) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    textAlign: 'center'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        fontFamily: Appearance.fonts.getFamilyForWeight(700),
                        fontSize: 24
                    }}>{'NO DEALERSHIP FOUND'}</span>
                    <span style={{
                        color: Appearance.colors.subText(),
                        fontFamily: Appearance.fonts.getFamilyForWeight(500),
                        fontSize: 16,
                        maxWidth: 500
                    }}>{'We were unable to locate a dealership using the supplied credentials. Please verify that the link you used is complete and valid.'}</span>
                </div>
            )
        }

        // determine which panels to show for a user that is already logged in
        let targets = panels[active.view] || [];
        targets = targets.filter(target => {
            if(typeof(target.visible) === 'function') {
                return target.visible(utils);
            }
            return true;
        });

        // Marketing views own their own top offset, the way the hero does, and
        // must not sit inside a bootstrap row — the legacy panels rely on both.
        let marketing = MarketingViews.includes(active.view);

        // see Catalog.isVisible - the quote list and coverage quiz go with the
        // product pages
        let showProducts = Catalog.isVisible(dealership);

        /*
         * The embedded app grows to the height of its own content and does not
         * scroll internally — this page is the only scroller, so the footer sits
         * below the app the way it sits below any other view. The frame's height
         * is driven by a  message from the app rather than by css, since
         * nothing out here can measure inside a cross-origin document.
         */
        let embedded = active.view === 'app';

        return (
            <animated.div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                {/* Header compares activeParams to decide which entry is
                    current - a product by its handle, an app section by its
                    section. Without them nothing in a submenu can highlight. */}
                <Header
                account={appNavigation}
                activeParams={active.params}
                activeRoute={waypoint || active.view}
                dealership={dealership}
                items={getNavigationItems().filter(item => item.selectable !== false)}
                onNavigate={onNavigationItemClick}
                onQuoteClick={showProducts ? onQuoteClick : null}
                onSignOut={onAppSignOut}

                // a quote list saved on an earlier visit would otherwise bring
                // the quote button back
                quoteCount={showProducts ? quote.count : 0} />
                {getLayers()}
                <div style={{
                    alignItems: 'center',
                    // Legacy pages draw near-white cards, so they sit on the
                    // site's off-white rather than the old body grey — their
                    // masthead is off-white too and the two now agree.
                    backgroundColor: marketing ? Theme.colors.white : Theme.colors.offWhite,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: marketing ? 0 : Theme.layout.headerHeight(),
                    position: 'relative',
                    width: '100%'
                }}>
                    <div
                    {...marketing === false && embedded === false && { className: 'row p-0 m-0' }}
                    style={{
                        position: 'relative',
                        width: '100%'
                    }}>
                        {targets.map(({ Component }, index) => (
                            <Component
                            dealership={dealership}
                            index={index}
                            key={index}
                            omnishield={omnishieldPreferences}
                            onAddToQuote={showProducts ? onAddToQuote : null}
                            onOpenBlindSpots={setLeadQuiz.bind(this, true)}
                            onOpenQuiz={showProducts ? onOpenQuiz : null}
                            params={active.params}
                            utils={utils}/>
                        ))}
                    </div>
                </div>
                <Footer
                dealership={dealership}
                onNavigate={onNavigationItemClick} />
                {/*
                    The affiliation disclosure, over the site rather than on the
                    About page. A disclosure a territory requires a visitor to
                    be given is not given by a card most of the way down a page
                    most visitors never open.
                */}
                <AffiliationBanner dealership={dealership} />
                {/* the embedded app's privacy prompt, rendered at the root so
                    it outlives any one page and sits above everything */}
                <ConsentModal />
            </animated.div>
        )
    }

    const getNavigationItems = () => {

        let { google_reviews = {} } = dealershipRef.current || {};
        if(!dealershipRef.current) {
            return [];
        }

        return [{
            key: 'home',
            title: 'Home',
            view: 'home',
            waypoint: 'home'
        },{
            // The products menu carries the catalogue this dealership actually
            // sells, then the walkthrough — browsing, then doing, with a rule
            // between them. The parent is still a link to the products page.
            items: Catalog.getAll(Catalog.getMode(dealershipRef.current)).map(product => ({
                key: 'products',
                params: { handle: product.handle },
                subtitle: product.subtitle,
                title: product.title,
                view: 'products'
            })).concat([{
                accent: true,
                divided: true,
                key: 'quiz',
                onClick: onOpenQuiz,
                title: 'Build your network ›'
            }]),
            key: 'products',
            title: 'Products',
            view: 'products',
            visible: Catalog.isVisible(dealershipRef.current)
        },{
            key: 'testimonials',
            title: 'Testimonials',
            view: 'testimonials',
            visible: google_reviews.enabled ? true : false
        },{
            key: 'promotions',
            title: 'Promotions',
            view: 'promotions',
            selectable: false
        },{
            key: 'careers',
            title: 'Careers',
            view: 'careers',
            selectable: false
        },{
            key: 'support',
            title: 'Support',
            view: 'support'
        },{
            key: 'about',
            title: 'About',
            view: 'about'
        },{
            // Privacy and terms live in the footer, listed individually. They
            // are documents people go looking for deliberately; a top-level menu
            // is for the handful of things we want someone to consider.
            key: 'contact',
            title: 'Contact',
            view: 'contact'
        }].filter(item => item.visible !== false);

        // the app is not in this list. it is reached through the account control
        // in the header instead - what is behind it is the visitor's own system
        // rather than another page of the storefront, and its sections only
        // exist once they have signed in.
    }

    const getTitleForView = view => {
        switch(view) {
            case 'emergency_contact_onboarding_survey':
            return 'Onboarding Survey';

            case 'global_data_survey':
            return 'Survey';

            default:
            return active.view?.split('_').map(string => Utils.ucFirst(string)).join(' ');
        }
    }

    const getViewForRoute = route => {

        // no compare is required if a route was not provided
        if(!route) {
            return null;
        }

        // loop through navigation items and find matching item
        let match = getNavigationItems().find(item => item.view === route || item.waypoint === route);
        return match && match.view;
    }

    // The single translation from a URL route to what is on screen. Preflight
    // and every later navigation both run through it, so a deep link and a
    // click on the same item cannot end up in different states.
    const getStateForRoute = route => {

        // follow any route that has moved, so links already in the wild
        // keep resolving
        let target = RouteAliases[route] || route;

        /*
         * A dealership that doesn't show the products has no product pages to
         * land on, including from a link someone saved or shared. The address
         * bar is corrected in place, without a new history entry, so the home
         * page isn't sitting under a products url and the back button doesn't
         * lead straight back to it.
         */
        if(target === 'products' && Catalog.isVisible(dealershipRef.current) === false) {
            window.history.replaceState({ route: null }, '', Route.href(null));
            return { view: 'home', waypoint: 'home' };
        }

        /*
         * Anything that resolves to a part of the support page rather than to
         * the top of it. See SupportRoutes.
         */
        if(SupportRoutes[target]) {
            return {
                supportFlow: SupportRoutes[target].flow || null,
                supportScroll: SupportRoutes[target].scroll || null,
                view: 'support',
                waypoint: 'support'
            };
        }

        // a direct route addresses its view rather than a waypoint on the
        // home page, and carries its own query parameters
        if(DirectRoutes.includes(target) === true) {
            return { view: target, waypoint: null };
        }

        // a view that is not a section of the home page has no waypoint to
        // scroll to, and setting one would leave the header highlighting a
        // nav item that is not showing
        if(panels[target] && target !== 'home') {
            return { view: target, waypoint: target };
        }
        return {
            view: getViewForRoute(target) || 'home',
            waypoint: target
        };
    }

    const runPreflightTasks = async () => {
        try {

            let { route } = QueryString.parse(window.location.search);

            // determine if a php error message was generated on page load
            let error = document.querySelector('meta[name="php_error_message"]');
            if(error) {
                throw new Error(error.getAttribute('content'));
            }

            // pull dealership id from meta tag, value was prepared by php script during page load
            let id = API.dev_env ? 321 : document.querySelector('meta[name="dealership_id"]').getAttribute('content');

            // fetch preferences for dealership using the web alias or custom domain
            let { access_token, dealership_id, google_business_profile, omnishield, preferences } = await Request.get(utils, '/dealerships/', {
                dealership_id: id,
                domain: window.location.hostname,
                type: 'replicated_website_preferences'
            });

            // update local state with omnishield preferences
            setOmnishieldPreferences(omnishield || {});

            // manually set dealership object for ref and update local state
            dealershipRef.current = { 
                ...preferences,
                access_token: access_token,
                google_business_profile: google_business_profile,
                id: dealership_id
            };

            // resolve the dealership's colors into the marketing surface
            // tokens before any of it is painted. Brand holds them at module
            // scope so Theme.js can read them without threading props.
            Brand.set(preferences.colors || {});

            // restore any quote this visitor already started with THIS
            // dealership, and bind it to the catalogue they are allowed to see
            Quote.init(dealership_id, Catalog.getMode(dealershipRef.current));

            setDealership(dealershipRef.current);

            // prepare view and waypoint for initial navigation
            let { supportFlow, supportScroll, view, waypoint } = getStateForRoute(route);

            // determine if a mobile app download request was submitted
            if(route === 'mobile_app_download') {

                // determine if the device is a mobile device or a tablet running ios
                let ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
                let mac = navigator.userAgent.includes('Macintosh');
                if(mac === false && (Utils.isMobile() === true || ios === true)) {
                    
                    // determine which type of url to prepare
                    let platform = ios ? 'ios' : 'android';
                    switch(platform) {
                        case 'android':
                        window.location.href = omnishield && omnishield.android.play_store_url || 'https://play.google.com/store/apps/details?id=com.commlink.omnishield2';
                        return;

                        case 'ios':
                        window.location.href = omnishield && omnishield.ios.app_store_url || 'https://apps.apple.com/gb/app/omnishield/id1491369651';
                        return;
                    }
                }

                // set waypoint to network/mobile app if device is a desktop device
                if(Utils.isMobile() === false || mac === true) {
                    waypoint = 'mobile_app';
                }
            }

            // set default active values and optional routing waypoint. A
            // retired route carries the node its replacement should open at.
            setActive({ params: { support_flow: supportFlow || null, support_scroll: supportScroll || null }, view });
            setWaypoint(waypoint);

            // amimate components into view
            setContentAnimations({ opacity: 1, top: 0 });


            // set preflight flag as complete
            setPreflight(true);

        } catch(e) {
            console.error(e.message);
            utils.loader.hide();
            utils.alert.show({
                title: 'Just a Second',
                message: e.message || 'An unknown error occurred'
            });
        }
    }

    const utils = {
        alert: {
            show: async props => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    }))
                } catch(e) {
                    console.log(e.message)
                }
            },
            dev: async () => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            title: 'In Development',
                            message: 'This feature is currently under development and will become available at a later date'
                        }]
                    }))

                } catch(e) {
                    console.log(e.message)
                }
            },
        },
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Dealership': `ID ${dealershipRef.current?.id || 0}`,
                    'X-Timezone': `TZ ${moment.tz.guess()}`,
                    'X-Web': `Build ${API.build}`
                }
            }
        },
        datePicker: {
            show: props => setDatePicker({
                utils: utils,
                id: `${moment().unix()}-${Math.random()}`,
                ...props
            })
        },
        dealership: {
            get: () => dealershipRef.current,
            omnishield: {
                get: () => omnishieldPreferencesRef.current
            }
        },
        events: EventsManager,
        layer: {
            open: layer => onOpenLayer(layer),
            close: layer => onCloseLayer(layer),
            requestClose: id => {
                let closeEvent = new CustomEvent('onLayerAction', {
                    detail: {
                        action: 'close',
                        layerID: id
                    }
                });
                window.dispatchEvent(closeEvent);
            }
        },
        loader: {
            show: async () => {
                return new Promise(resolve => {
                    setLoader(true);
                    setTimeout(resolve, 500)
                });
            },
            hide: async () => {
                return new Promise(resolve => {
                    setLoader(false);
                    setTimeout(resolve, 500)
                });
            }
        },
        navigation: {
            set: onNavigationItemClick,
            setWaypointObserver: (target, identifier) => {

                // create interaction observer for element
                let observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        if(entry.intersectionRatio > 0) {
                            setWaypoint(identifier);
                        }
                    });
                }, {
                    rootMargin: '0px',
                    threshold: 0
                });
        
                // add observer to component
                observer.observe(target);
            }
        },
        sheet: {
            show: (sheet, callback) => {
                setSheet({ ...sheet, onClick: callback })
            }
        },
        stripe: Stripe
    }

    useEffect(() => {
        onActiveViewChange(activeRef.current);
    }, [active, dealership]);

    useEffect(() => {
        dealershipRef.current = dealership;
        onSetStyleSheetProperties();
    }, [dealership]);

    useEffect(() => {
        layersRef.current = layers;
    }, [layers]);

    useEffect(() => {
        omnishieldPreferencesRef.current = omnishieldPreferences;
    }, [omnishieldPreferences]);

    // The URL drives the active view, including the back and forward buttons.
    useEffect(() => {
        AppNavigation.subscribe(setAppNavigation);

        let { params: routeParams } = Route.get();
        if(routeParams?.quiz) {
            setLeadQuiz(true);
        }

        return Route.subscribe(onRouteChange);
    }, [dealership]);

    useEffect(() => {
        return Quote.subscribe(setQuote);
    }, []);

    useEffect(() => {

        runPreflightTasks();

        // setup window listeners and smoothscroll polyfill
        smoothscroll.polyfill();
        window.addEventListener('resize', onSizeChange);
        window.addEventListener('beforeunload', evt => {
            evt.returnValue = null;
            return null;
        });

        // css variables
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());

        // The site is pinned to the light theme. The marketing surface is a
        // light-only design and a white header above a dark legacy panel is a
        // visible seam, so the whole site commits rather than half of it. The
        // plumbing below is left intact so this is one line to reverse.
        window.theme = 'light';
        document.documentElement.style.setProperty('--theme', window.theme);
        onSetStyleSheetProperties();

    }, []);

    return (
        <div
        className={`root-container ${theme}`}
        nonce={nonce}
        style={{
            height: size.height,
            position: 'relative',
            width: size.width
        }}>
            {getMainContent()}

            {/* Drawn outside the main content so they sit above the fixed
                header and survive a view change mid-quote */}
            {dealership && (
                <>
                    <LeadQuizModal
                    dealership={dealership}
                    onClose={setLeadQuiz.bind(this, false)}
                    open={leadQuiz === true}
                    utils={utils} />

                    {/* The quote list, the coverage quiz that fills it, and the
                        request form it ends in are the product checkout. Not
                        mounting them at all means nothing left on the page -
                        a saved list, a stray handler - can open one. */}
                    {Catalog.isVisible(dealership) && (
                        <>
                            <QuoteDrawer
                            items={quote.items}
                            onClose={setQuoteOpen.bind(this, false)}
                            onRemoveItem={Quote.removeItem}
                            onRequestQuote={onRequestQuote}
                            onUpdateItem={Quote.updateItem}
                            open={quoteOpen} />

                            <QuizModal
                            dealership={dealership}
                            onAddPlanToQuote={onAddPlanToQuote}
                            onClose={setQuizOpen.bind(this, false)}
                            open={quizOpen}
                            quiz={quote.quiz} />

                            <QuoteRequestModal
                            dealership={dealership}
                            items={quote.items}
                            onClose={setQuoteModalOpen.bind(this, false)}
                            onSubmitted={onQuoteSubmitted}
                            open={quoteModalOpen}
                            quiz={quote.quiz}
                            utils={utils} />
                        </>
                    )}
                </>
            )}

            {datePicker && (
                <DatePicker
                {...datePicker}
                onClose={() => {
                    setDatePicker(null);
                    if(typeof(datePicker.onClose) === 'function') {
                        datePicker.onClose();
                    }
                }} />
            )}
            {sheet && (
                <Sheet
                {...sheet}
                onClose={() => setSheet(null)}/>
            )}
            <AlertStack>
                {alerts.map((alert, index) => (
                    <Alert
                    {...alert}
                    key={index}
                    utils={utils}
                    index={(alerts.length - 1) - index}
                    onClose={id => {
                        if(typeof(alert.onClose) === 'function') {
                            alert.onClose();
                        }
                        setAlerts(alerts => {
                            return alerts.filter(alert => {
                                return id !== alert.id;
                            });
                        })
                    }} />
                ))}
            </AlertStack>
        </div>
    )
}

export default App;
